import { Header, Icon, Sheet } from "@caint/design-system";
import { Fragment, useState } from "react";
import "../styles/menu.css";
import { AssinaturaCampos } from "../components/AssinaturaCampos.tsx";
import { TrocarSenhaCampos } from "../components/TrocarSenhaCampos.tsx";
import { GRUPOS_NAV, type GrupoNav, filtrarGrupos, gruposVisiveis, iniciais } from "../lib/navegacao.ts";
import { useAssinatura } from "../lib/useAssinatura.ts";
import { useTrocarSenha } from "../lib/useTrocarSenha.ts";
import type { SyncStatus, UsuarioAutenticado } from "../state/useAppState.ts";

export interface MaisProps {
  auth: UsuarioAutenticado;
  online: boolean;
  pendingSync: number;
  syncStatus: SyncStatus;
  sync: () => Promise<void>;
  /** Recupera respostas presas no aparelho: re-enfileira o ciclo atual e sincroniza. */
  reenviarRespostasLocais: () => void;
  /** Há respostas locais no ciclo atual (habilita o reenvio manual). */
  temRespostasLocais: boolean;
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
  token: string | null;
  go: (route: string, arg?: string) => void;
  onLogout: () => void;
}

function AssinaturaSheet({ token, onClose }: { token: string | null; onClose: () => void }) {
  const f = useAssinatura(token);
  return (
    <Sheet
      title="Minha assinatura"
      sub="Imagem que aparece no Relatório da Unidade"
      onClose={onClose}
      foot={
        <button type="button" className="m-btn primary" onClick={onClose}>
          Fechar
        </button>
      }
    >
      <AssinaturaCampos f={f} />
    </Sheet>
  );
}

function TrocarSenhaSheet({
  trocarSenha,
  onClose,
}: {
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
  onClose: () => void;
}) {
  const f = useTrocarSenha(trocarSenha);
  return (
    <Sheet
      title="Trocar senha"
      sub="Atualize a senha da sua conta"
      onClose={onClose}
      foot={
        f.sucesso ? (
          <button type="button" className="m-btn primary" onClick={onClose}>
            Concluído
          </button>
        ) : (
          <button
            type="button"
            className="m-btn primary"
            disabled={f.enviando}
            onClick={() => void f.submeter()}
          >
            {f.enviando ? "Salvando…" : "Salvar nova senha"}
          </button>
        )
      }
    >
      <TrocarSenhaCampos f={f} />
    </Sheet>
  );
}

/** Um grupo do hub: cartões (Monitoramento) ou lista (demais). */
function GrupoHub({ g, go }: { g: GrupoNav; go: (rota: string) => void }) {
  const emCartoes = g.id === "monitoramento";
  return (
    <Fragment>
      <div className="mh-gt">{g.nome}</div>
      {emCartoes ? (
        <div className="mh-tiles">
          {g.itens.map((it) => (
            <button key={it.rota} type="button" className="mh-tile" onClick={() => go(it.rota)}>
              <span className="mh-ti" style={{ background: g.cor }}>
                <Icon name={it.icone} size={20} color="#fff" />
              </span>
              <b>{it.titulo}</b>
              <span>{it.sub}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="mh-list">
          {g.itens.map((it) => (
            <button key={it.rota} type="button" className="mh-row" onClick={() => go(it.rota)}>
              <span className="mh-ri" style={{ background: g.cor }}>
                <Icon name={it.icone} size={17} color="#fff" />
              </span>
              <span className="mh-rm">
                <b>{it.titulo}</b>
                <span>{it.sub}</span>
              </span>
              <Icon name="chevron" size={15} color="var(--text-faint)" />
            </button>
          ))}
        </div>
      )}
    </Fragment>
  );
}

export function Mais({
  auth,
  online,
  pendingSync,
  syncStatus,
  sync,
  reenviarRespostasLocais,
  temRespostasLocais,
  trocarSenha,
  token,
  go,
  onLogout,
}: MaisProps) {
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
  const [busca, setBusca] = useState("");
  // Avaliação já está nas abas inferiores; o hub organiza o restante (por perfil).
  const gruposMais = gruposVisiveis(GRUPOS_NAV, auth.papel).filter((g) => g.id !== "avaliacao");
  const grupos = filtrarGrupos(gruposMais, busca);

  return (
    <Fragment>
      <Header title="Mais" />
      <div className="m-body">
        <div className="mh-prof">
          <span className="mh-av">{iniciais(auth.nome)}</span>
          <div className="mh-pi">
            <b>{auth.nome}</b>
            <span>{auth.funcao}</span>
          </div>
        </div>
        <div className="mh-status">
          <span
            className="m-pill"
            style={{
              background: online ? "var(--mat-3-soft)" : "var(--surface-2)",
              color: online ? "var(--mat-4)" : "var(--text-muted)",
              fontSize: 11,
            }}
          >
            {online ? "● online" : "○ offline"}
          </span>
          <span className="spec">
            {syncStatus.estado === "sincronizando"
              ? "sincronizando…"
              : pendingSync > 0
                ? `${pendingSync} alteração(ões) na fila`
                : "tudo sincronizado"}
          </span>
          {pendingSync > 0 && online && (
            <button
              type="button"
              className="m-btn"
              disabled={syncStatus.estado === "sincronizando"}
              onClick={() => void sync()}
            >
              {syncStatus.estado === "sincronizando" ? "Sincronizando…" : "Sincronizar"}
            </button>
          )}
        </div>
        {(syncStatus.estado === "erro" || syncStatus.estado === "retido") && syncStatus.mensagem && (
          <div
            className="spec"
            style={{
              color: syncStatus.estado === "erro" ? "var(--danger)" : "var(--warning)",
              marginBottom: 8,
            }}
            role="alert"
          >
            {syncStatus.estado === "erro" ? "⚠️" : "⏳"} {syncStatus.mensagem}
          </div>
        )}
        {temRespostasLocais && online && (
          <button
            type="button"
            className="m-btn"
            style={{ width: "100%", marginBottom: 6 }}
            disabled={syncStatus.estado === "sincronizando"}
            title="Reenvia ao servidor as respostas deste ciclo salvas neste aparelho (recuperação)"
            onClick={() => reenviarRespostasLocais()}
          >
            Reenviar respostas do ciclo
          </button>
        )}
        <button
          type="button"
          className="m-btn"
          style={{ width: "100%", marginBottom: 6 }}
          onClick={() => setMostrarSenha(true)}
        >
          Trocar senha
        </button>
        <button
          type="button"
          className="m-btn"
          style={{ width: "100%", marginBottom: 6 }}
          onClick={() => setMostrarAssinatura(true)}
        >
          Minha assinatura
        </button>

        <div className="mh-search">
          <Icon name="search" size={16} />
          <input
            type="search"
            placeholder="Buscar no menu…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        {grupos.map((g) => (
          <GrupoHub key={g.id} g={g} go={go} />
        ))}
        {grupos.length === 0 && (
          <p className="spec" style={{ textAlign: "center", padding: "20px 0" }}>
            Nada encontrado para “{busca.trim()}”.
          </p>
        )}

        <button type="button" className="m-btn mh-logout" onClick={onLogout}>
          <Icon name="logout" size={17} />
          Sair
        </button>
      </div>
      {mostrarSenha && <TrocarSenhaSheet trocarSenha={trocarSenha} onClose={() => setMostrarSenha(false)} />}
      {mostrarAssinatura && <AssinaturaSheet token={token} onClose={() => setMostrarAssinatura(false)} />}
    </Fragment>
  );
}
